import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router';
import {
  Users,
  Mail,
  FileText,
  TrendingUp,
  Sparkles,
  Send,
  ArrowRight,
  CheckCircle2,
  PartyPopper,
  X,
  Clock,
  ChevronRight,
  Zap,
  BarChart3,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import { apiGet, apiPost, apiPatch } from '@/lib/api';
import { useCountUp } from '@/hooks/useCountUp';
import { useAuth } from '@/hooks/useAuth';
import { syncReplies } from '@/lib/extension-bridge';

/* ---------- helpers ---------- */

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function getFirstName(email?: string | null): string {
  if (!email) return '';
  const local = email.split('@')[0];
  // capitalize first letter of the local part
  return local.charAt(0).toUpperCase() + local.slice(1).split(/[._+\-]/)[0];
}

/* ---------- stat card ---------- */

function StatCard({
  icon: Icon,
  label,
  value,
  suffix,
  color = 'primary',
}: {
  icon: typeof Users;
  label: string;
  value: number;
  suffix?: string;
  color?: string;
}) {
  const animatedValue = useCountUp(value);
  return (
    <div className="glass-card rounded-xl p-4 animate-fade-in group hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg bg-${color}/10 flex items-center justify-center shrink-0`}>
          <Icon size={18} className={`text-${color}`} />
        </div>
        <div className="min-w-0">
          <p className="text-2xl font-bold tabular-nums tracking-tight leading-none">
            {animatedValue}{suffix}
          </p>
          <p className="text-xs text-base-content/40 mt-0.5">{label}</p>
        </div>
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
}

/* ---------- plan & usage card ---------- */

function PlanUsageCard({
  billing,
  upgrading,
  setUpgrading,
}: {
  billing: BillingInfo;
  upgrading: boolean;
  setUpgrading: (v: boolean) => void;
}) {
  const unlimited = billing.tokensLimit === -1;
  const pct = !unlimited && billing.tokensLimit > 0
    ? Math.min((billing.tokensUsed / billing.tokensLimit) * 100, 100)
    : 0;
  const isNear = !unlimited && pct >= 80;
  const isOver = !unlimited && pct >= 100;
  const isFree = billing.planId === 'free';

  async function handleUpgrade() {
    setUpgrading(true);
    try {
      const plans = await apiGet<{ id: string; polar_product_id: string | null }[]>('/billing/plans');
      const pro = plans.find((p) => p.id !== 'free' && p.polar_product_id);
      if (pro?.polar_product_id) {
        const { url } = await apiPost<{ url: string }>('/billing/checkout', { productId: pro.polar_product_id });
        window.location.href = url;
      }
    } catch {}
    setUpgrading(false);
  }

  return (
    <div>
      <h2 className="text-xs font-semibold uppercase tracking-wider text-base-content/40 mb-3 px-1">
        Plan & Usage
      </h2>
      <div className="glass-card rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Zap size={18} className="text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold">{billing.planName}</p>
              <p className="text-xs text-base-content/40">
                {isFree ? 'Free tier' : `€${(billing.priceCents / 100).toFixed(0)}/mo`}
              </p>
            </div>
          </div>
          {isFree ? (
            <button
              onClick={handleUpgrade}
              disabled={upgrading}
              className="btn btn-primary btn-sm gap-1"
            >
              Upgrade <ArrowRight size={12} />
            </button>
          ) : (
            <Link to="/settings/billing" className="btn btn-ghost btn-sm gap-1 text-base-content/50">
              Manage <ExternalLink size={12} />
            </Link>
          )}
        </div>

        {/* Token usage bar */}
        {!unlimited ? (
          <>
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-base-content/50 flex items-center gap-1">
                <BarChart3 size={12} />
                Token usage
              </span>
              <span className={`tabular-nums ${isOver ? 'text-error font-medium' : isNear ? 'text-warning font-medium' : 'text-base-content/40'}`}>
                {formatTokens(billing.tokensUsed)} / {formatTokens(billing.tokensLimit)}
              </span>
            </div>
            <div className="w-full h-2 rounded-full bg-base-300 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${
                  isOver ? 'bg-error' : isNear ? 'bg-warning' : 'bg-primary'
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-[11px] text-base-content/30 mt-1.5">
              {Math.round(pct)}% used this period
            </p>
          </>
        ) : (
          <div className="flex items-center gap-2 text-xs text-base-content/40">
            <BarChart3 size={12} />
            <span>{formatTokens(billing.tokensUsed)} tokens used — unlimited plan</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- subscription success modal ---------- */

function SubscriptionSuccessModal({ onClose }: { onClose: () => void }) {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 50);
    return () => clearTimeout(t1);
  }, []);

  function handleClose() {
    setStage(2);
    setTimeout(onClose, 300);
  }

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
        stage === 0 ? 'bg-black/0' : stage === 2 ? 'bg-black/0' : 'bg-black/50'
      }`}
      onClick={handleClose}
    >
      <div
        className={`relative glass-card rounded-2xl p-8 max-w-sm w-full mx-4 text-center space-y-5 transition-all duration-500 ${
          stage === 1
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-90 translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 p-1 rounded-lg hover:bg-base-300 transition-colors text-base-content/40 hover:text-base-content"
        >
          <X size={16} />
        </button>
        <div className="flex justify-center">
          <div className={`w-20 h-20 rounded-full bg-success/15 flex items-center justify-center transition-all duration-700 ${
            stage === 1 ? 'scale-100' : 'scale-0'
          }`}>
            <CheckCircle2
              size={44}
              className={`text-success transition-all duration-500 delay-200 ${
                stage === 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
              }`}
            />
          </div>
        </div>
        <div className={`space-y-2 transition-all duration-500 delay-300 ${
          stage === 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`}>
          <h2 className="text-xl font-bold flex items-center justify-center gap-2">
            Welcome to Pro! <PartyPopper size={20} className="text-warning" />
          </h2>
          <p className="text-sm text-base-content/50">
            Your subscription is active. You now have access to 10,000 tokens per month. Start creating!
          </p>
        </div>
        <div className={`transition-all duration-500 delay-500 ${
          stage === 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`}>
          <button onClick={handleClose} className="btn btn-primary btn-sm w-full">
            Let's go!
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- main page ---------- */

interface BillingInfo {
  planName: string;
  planId: string;
  tokensUsed: number;
  tokensLimit: number;
  priceCents: number;
}

export function WorkspacePage() {
  const [stats, setStats] = useState({ leads: 0, emails: 0, drafts: 0, replyRate: 0 });
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (searchParams.get('billing') === 'success') {
      setShowSuccess(true);
      navigate('/dashboard', { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    async function loadData() {
      try {
        const [statsData, subData, usageData] = await Promise.all([
          apiGet<{ totalLeads: number; emailsGenerated: number; draftsCreated: number; replyRate: number }>('/tenants/stats'),
          apiGet<{ subscription: { planId: string; tokensUsed: number; tokensLimit: number }; plan: { name: string; priceCents: number } | null }>('/billing/subscription'),
          apiGet<{ tokens: { used: number; limit: number } }>('/billing/usage'),
        ]);
        setStats({
          leads: statsData.totalLeads,
          emails: statsData.emailsGenerated,
          drafts: statsData.draftsCreated,
          replyRate: statsData.replyRate ?? 0,
        });
        setBilling({
          planName: subData.plan?.name ?? 'Free',
          planId: subData.subscription.planId || 'free',
          tokensUsed: usageData.tokens.used,
          tokensLimit: usageData.tokens.limit,
          priceCents: subData.plan?.priceCents ?? 0,
        });
      } catch (err) {
        console.error('[dashboard] Failed to load stats:', err);
      }
      setLoaded(true);
    }
    loadData();
  }, []);

  // Reply sync — callable manually or on load
  async function doReplySync() {
    setSyncing(true);
    try {
      // 1. Ask backend for all sent leads that haven't been marked as replied yet
      const sentLeads = await apiGet<{ leadId: string; recipientEmail: string; subject: string }[]>('/leads/sent-for-scan');

      if (sentLeads.length > 0) {
        // 2. Ask extension to scan Gmail inbox for replies to these leads
        const replies = await syncReplies(sentLeads);

        // 3. Report any found replies back to the backend
        if (replies.length > 0) {
          await apiPatch('/leads/replies', { replies });
        }
      }

      // 4. Always refresh stats so user sees latest reply rate
      const statsData = await apiGet<{ totalLeads: number; emailsGenerated: number; draftsCreated: number; replyRate: number }>('/tenants/stats');
      setStats({
        leads: statsData.totalLeads,
        emails: statsData.emailsGenerated,
        drafts: statsData.draftsCreated,
        replyRate: statsData.replyRate ?? 0,
      });
    } catch {
      // Silent — reply sync is best-effort
    }
    setSyncing(false);
  }

  // Auto-sync on dashboard load
  useEffect(() => {
    doReplySync();
  }, []);

  const hasActivity = stats.leads > 0 || stats.emails > 0 || stats.drafts > 0;

  return (
    <div className="max-w-3xl mx-auto w-full animate-fade-in">
      {showSuccess && <SubscriptionSuccessModal onClose={() => setShowSuccess(false)} />}

      {/* Greeting */}
      <div className="mb-10">
        <p className="text-sm text-base-content/40 flex items-center gap-1.5 mb-1">
          <Clock size={12} />
          {getGreeting()}
        </p>
        <h1 className="text-2xl font-extrabold tracking-tight">
          {getFirstName(user?.email) ? `Hey ${getFirstName(user?.email)}` : 'Workspace'}
        </h1>
      </div>

      {/* Start Outreach — hero CTA */}
      <div className="text-center mb-12">
        <Link
          to="/leads"
          className="inline-flex items-center justify-center gap-2.5 px-10 py-4 rounded-2xl bg-gradient-to-r from-primary to-purple-500 text-white shadow-lg hover:shadow-xl hover:shadow-primary/25 hover:scale-[1.03] transition-all text-base font-semibold"
        >
          <ArrowRight size={18} />
          Start Outreach
        </Link>
        <p className="text-xs text-base-content/30 mt-3">
          Find leads, personalize emails, send through Gmail
        </p>
      </div>

      {/* Stats grid */}
      {loaded && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-12">
          <StatCard icon={Users} label="Total Leads" value={stats.leads} />
          <StatCard icon={Mail} label="Emails Generated" value={stats.emails} />
          <StatCard icon={FileText} label="Drafts Created" value={stats.drafts} />
          <div className="glass-card rounded-xl p-4 animate-fade-in group hover:shadow-md transition-shadow relative">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <TrendingUp size={18} className="text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-2xl font-bold tabular-nums tracking-tight leading-none">
                  {stats.replyRate}%
                </p>
                <p className="text-xs text-base-content/40 mt-0.5">Reply Rate</p>
              </div>
              <button
                onClick={doReplySync}
                disabled={syncing}
                title="Sync replies from Gmail"
                className="w-7 h-7 rounded-lg flex items-center justify-center text-base-content/30 hover:text-primary hover:bg-primary/10 transition-all disabled:opacity-50"
              >
                <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="mb-12">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-base-content/40 mb-3 px-1">
          Quick Actions
        </h2>
        <div className="grid sm:grid-cols-2 gap-2">
          <Link
            to="/leads"
            className="glass-card rounded-xl p-4 flex items-center gap-3 hover:shadow-md transition-all group block"
          >
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Sparkles size={16} className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Generate Leads with AI</p>
              <p className="text-xs text-base-content/40">Describe your target audience</p>
            </div>
            <ChevronRight size={14} className="text-base-content/20 group-hover:text-base-content/50 transition-colors" />
          </Link>

          <Link
            to="/leads"
            className="glass-card rounded-xl p-4 flex items-center gap-3 hover:shadow-md transition-all group block"
          >
            <div className="w-9 h-9 rounded-lg bg-success/10 flex items-center justify-center shrink-0">
              <FileText size={16} className="text-success" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Upload CSV</p>
              <p className="text-xs text-base-content/40">Import your own lead list</p>
            </div>
            <ChevronRight size={14} className="text-base-content/20 group-hover:text-base-content/50 transition-colors" />
          </Link>

          <Link
            to="/draft-emails"
            className="glass-card rounded-xl p-4 flex items-center gap-3 hover:shadow-md transition-all group block"
          >
            <div className="w-9 h-9 rounded-lg bg-warning/10 flex items-center justify-center shrink-0">
              <Mail size={16} className="text-warning" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Draft Emails</p>
              <p className="text-xs text-base-content/40">Personalize & generate</p>
            </div>
            <ChevronRight size={14} className="text-base-content/20 group-hover:text-base-content/50 transition-colors" />
          </Link>

          <Link
            to="/review-send"
            className="glass-card rounded-xl p-4 flex items-center gap-3 hover:shadow-md transition-all group block"
          >
            <div className="w-9 h-9 rounded-lg bg-info/10 flex items-center justify-center shrink-0">
              <Send size={16} className="text-info" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Review & Send</p>
              <p className="text-xs text-base-content/40">Send through Gmail</p>
            </div>
            <ChevronRight size={14} className="text-base-content/20 group-hover:text-base-content/50 transition-colors" />
          </Link>
        </div>
      </div>

      {/* Plan & Usage */}
      {billing && <PlanUsageCard billing={billing} upgrading={upgrading} setUpgrading={setUpgrading} />}

      {/* Empty state hint */}
      {loaded && !hasActivity && (
        <div className="mt-10 text-center">
          <p className="text-xs text-base-content/30">
            Start your first outreach to see your stats come alive.
          </p>
        </div>
      )}
    </div>
  );
}
